import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select as ChakraSelect,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React from "react";
import { FiFilter } from "react-icons/fi";
import { HiOutlineSearch, HiOutlineX } from "react-icons/hi";
import Select, { Theme } from "react-select";

import { Button, useTheme } from "../../../components";
import useSelectTheme from "../../../hooks/useSelectTheme";
import {
  AiInterviewerCreatedDateRange,
  AiInterviewersFilterOptionsQuery,
  AiInterviewerStatus,
} from "../../graphql";
import {
  FilterOption,
  UseAiInterviewersFiltersResult,
} from "./useAiInterviewersFilters";

export const useAiInterviewersFiltersSelectTheme = (): [
  (theme: Theme) => Theme,
  Record<string, any>
] => {
  const { colors } = useTheme();
  const selectValueStyles = {
    fontSize: "14px",
    color: colors.gray[900],
  };
  const [selectTheme, selectStyles] = useSelectTheme({
    control: (provided: Record<string, any>, state: { isFocused: any }) => ({
      ...provided,
      borderColor: colors.gray[200],
      borderRadius: "6px",
      height: "40px",
    }),
    menu: (provided: Record<string, any>) => ({
      ...provided,
      fontSize: "14px",
    }),
    placeholder: (provided: Record<string, any>) => ({
      ...provided,
      color: colors.gray[900],
    }),
    singleValue: (provided: Record<string, any>) => ({
      ...provided,
      ...selectValueStyles,
    }),
    valueContainer: (provided: Record<string, any>) => ({
      ...provided,
      ...selectValueStyles,
    }),
    indicatorSeparator: () => ({ display: "none" }),
  });
  return [selectTheme, selectStyles];
};

const statusLabelByValue: Record<AiInterviewerStatus, string> = {
  [AiInterviewerStatus.Live]: "Live",
  [AiInterviewerStatus.Draft]: "Draft",
  [AiInterviewerStatus.Archived]: "Archived",
};

const createdDateLabelByValue: Record<AiInterviewerCreatedDateRange, string> = {
  [AiInterviewerCreatedDateRange.PastWeek]: "Past Week",
  [AiInterviewerCreatedDateRange.PastMonth]: "Past Month",
  [AiInterviewerCreatedDateRange.PastYear]: "Past Year",
};

const statusOptions: FilterOption[] = [
  { value: AiInterviewerStatus.Live, label: "Live" },
  { value: AiInterviewerStatus.Draft, label: "Draft" },
  { value: AiInterviewerStatus.Archived, label: "Archived" },
];

const createdDateOptions: FilterOption[] = [
  {
    value: AiInterviewerCreatedDateRange.PastWeek,
    label: "Past Week",
  },
  {
    value: AiInterviewerCreatedDateRange.PastMonth,
    label: "Past Month",
  },
  {
    value: AiInterviewerCreatedDateRange.PastYear,
    label: "Past Year",
  },
];

type AiInterviewersFiltersProps = {
  filters: UseAiInterviewersFiltersResult;
  allAiInterviewers: NonNullable<
    AiInterviewersFilterOptionsQuery["aiInterviewers"]
  >;
  displayedCount: number;
  totalCountWithoutFilters: number;
  isSmallScreen: boolean;
  loading: boolean;
};

export const AiInterviewersFilters: React.FC<AiInterviewersFiltersProps> = ({
  filters,
  allAiInterviewers,
  displayedCount,
  totalCountWithoutFilters,
  isSmallScreen,
  loading,
}) => {
  const [selectTheme, selectStyles] = useAiInterviewersFiltersSelectTheme();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const knownPositionLabelsById = React.useMemo(() => {
    const labelsById: Record<string, string> = {};

    allAiInterviewers.forEach((interviewer) => {
      const positionId = interviewer.position?.id;
      const positionTitle = interviewer.position?.displayTitle;

      if (!positionId || !positionTitle) {
        return;
      }

      labelsById[positionId] = positionTitle;
    });

    return labelsById;
  }, [allAiInterviewers]);

  const knownAuthorLabelsById = React.useMemo(() => {
    const labelsById: Record<string, string> = {};

    allAiInterviewers.forEach((interviewer) => {
      const authorId = interviewer.currentVersion.createdBy.id;
      const authorName = interviewer.currentVersion.createdBy.fullName;

      if (!authorName) {
        return;
      }

      labelsById[authorId] = authorName;
    });

    return labelsById;
  }, [allAiInterviewers]);

  const positionOptions = React.useMemo(
    () =>
      Object.entries(knownPositionLabelsById).sort((left, right) =>
        left[1].localeCompare(right[1])
      ),
    [knownPositionLabelsById]
  );

  const authorOptions = React.useMemo(
    () =>
      Object.entries(knownAuthorLabelsById).sort((left, right) =>
        left[1].localeCompare(right[1])
      ),
    [knownAuthorLabelsById]
  );

  const positionSelectOptions: FilterOption[] = React.useMemo(
    () =>
      positionOptions.map(([id, label]) => ({
        value: id,
        label,
      })),
    [positionOptions]
  );

  const authorSelectOptions: FilterOption[] = React.useMemo(
    () =>
      authorOptions.map(([id, label]) => ({
        value: id,
        label,
      })),
    [authorOptions]
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        filters.statusFilters.includes(option.value as AiInterviewerStatus)
      ),
    [filters.statusFilters]
  );

  const selectedPositionOptions = React.useMemo(
    () =>
      filters.positionIdFilters.map((positionId) => ({
        value: positionId,
        label: knownPositionLabelsById[positionId] || positionId,
      })),
    [filters.positionIdFilters, knownPositionLabelsById]
  );

  const selectedAuthorOptions = React.useMemo(
    () =>
      filters.authorIdFilters.map((authorId) => ({
        value: authorId,
        label: knownAuthorLabelsById[authorId] || authorId,
      })),
    [filters.authorIdFilters, knownAuthorLabelsById]
  );

  const selectedCreatedDateOption = React.useMemo(
    () =>
      createdDateOptions.find(
        (option) => option.value === filters.createdDateFilter
      ) || null,
    [filters.createdDateFilter]
  );

  const drawerSelectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        filters.drawerStatusFilters.includes(
          option.value as AiInterviewerStatus
        )
      ),
    [filters.drawerStatusFilters]
  );

  const drawerSelectedPositionOptions = React.useMemo(
    () =>
      filters.drawerPositionFilters.map((positionId) => ({
        value: positionId,
        label: knownPositionLabelsById[positionId] || positionId,
      })),
    [filters.drawerPositionFilters, knownPositionLabelsById]
  );

  const drawerSelectedAuthorOptions = React.useMemo(
    () =>
      filters.drawerAuthorFilters.map((authorId) => ({
        value: authorId,
        label: knownAuthorLabelsById[authorId] || authorId,
      })),
    [filters.drawerAuthorFilters, knownAuthorLabelsById]
  );

  const drawerSelectedCreatedDateOption = React.useMemo(
    () =>
      createdDateOptions.find(
        (option) => option.value === filters.drawerDate
      ) || null,
    [filters.drawerDate]
  );

  const mobileFilterPills = React.useMemo(() => {
    const pills: Array<{ key: string; label: string; onRemove: () => void }> =
      [];

    if (filters.keywordFilter) {
      pills.push({
        key: "keyword",
        label: `Keyword: ${filters.keywordFilter}`,
        onRemove: () => filters.setSearchTerm(""),
      });
    }

    filters.statusFilters.forEach((status) => {
      pills.push({
        key: `status-${status}`,
        label: statusLabelByValue[status] || status,
        onRemove: () =>
          filters.setStatusFilters((currentFilters) =>
            currentFilters.filter((currentStatus) => currentStatus !== status)
          ),
      });
    });

    filters.positionIdFilters.forEach((positionId) => {
      pills.push({
        key: `position-${positionId}`,
        label: knownPositionLabelsById[positionId] || positionId,
        onRemove: () =>
          filters.setPositionIdFilters((currentFilters) =>
            currentFilters.filter(
              (currentPositionId) => currentPositionId !== positionId
            )
          ),
      });
    });

    filters.authorIdFilters.forEach((authorId) => {
      pills.push({
        key: `author-${authorId}`,
        label: knownAuthorLabelsById[authorId] || authorId,
        onRemove: () =>
          filters.setAuthorIdFilters((currentFilters) =>
            currentFilters.filter(
              (currentAuthorId) => currentAuthorId !== authorId
            )
          ),
      });
    });

    if (filters.createdDateFilter) {
      pills.push({
        key: "createdDate",
        label:
          createdDateLabelByValue[filters.createdDateFilter] ||
          filters.createdDateFilter,
        onRemove: () => filters.setCreatedDateFilter(""),
      });
    }

    return pills;
  }, [
    filters.keywordFilter,
    filters.setSearchTerm,
    filters.statusFilters,
    filters.setStatusFilters,
    filters.positionIdFilters,
    filters.setPositionIdFilters,
    filters.authorIdFilters,
    filters.setAuthorIdFilters,
    filters.createdDateFilter,
    filters.setCreatedDateFilter,
    knownPositionLabelsById,
    knownAuthorLabelsById,
  ]);

  const handleOpenDrawer = () => {
    filters.syncDrawerFromFilters();
    onOpen();
  };

  const handleApplyDrawerFilters = () => {
    filters.applyDrawerFilters();
    onClose();
  };

  const handleResetDrawerFilters = () => {
    filters.resetAllFiltersAndDrawer();
    onClose();
  };

  return (
    <>
      {isSmallScreen ? (
        <Box bg="gray.50" borderRadius="lg" p={4} mb="4">
          <HStack justify="space-between" alignItems="center">
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {displayedCount} of {totalCountWithoutFilters} AI interviewers
            </Text>
            <Button
              leftIcon={<FiFilter size={18} />}
              onClick={handleOpenDrawer}
              variant="ghost"
              colorScheme="blue"
              size="sm"
              fontWeight="medium"
            >
              Filters
              {filters.activeFilterCount > 0
                ? ` (${filters.activeFilterCount})`
                : ""}
            </Button>
          </HStack>
          {mobileFilterPills.length > 0 && (
            <Wrap spacing={2} mt={3}>
              {mobileFilterPills.map((pill) => (
                <WrapItem key={pill.key}>
                  <Tag size="md" variant="subtle" colorScheme="blue">
                    <TagLabel>{pill.label}</TagLabel>
                    <TagCloseButton onClick={pill.onRemove} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>
      ) : (
        <Flex
          alignItems="center"
          direction="row"
          gap="4"
          pb="4"
          borderBottomWidth="1px"
          borderBottomColor="gray.200"
        >
          <Icon as={FiFilter} boxSize="5" color="gray.500" />
          <Flex flex="1" alignItems="center" direction="row" gap="4">
            <Box flex="1" minW="0">
              <Select
                theme={selectTheme}
                styles={selectStyles}
                value={selectedStatusOptions}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                options={statusOptions}
                placeholder="All statuses"
                onChange={filters.handleStatusChange}
              />
            </Box>
            <Box flex="1" minW="0">
              <Select
                theme={selectTheme}
                styles={selectStyles}
                value={selectedPositionOptions}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                options={positionSelectOptions}
                placeholder="All positions"
                onChange={filters.handlePositionChange}
              />
            </Box>
            <Box flex="1" minW="0">
              <Select
                theme={selectTheme}
                styles={selectStyles}
                value={selectedAuthorOptions}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                options={authorSelectOptions}
                placeholder="All authors"
                onChange={filters.handleAuthorChange}
              />
            </Box>
            <Box flex="1" minW="0">
              <Select
                theme={selectTheme}
                styles={selectStyles}
                value={selectedCreatedDateOption}
                isClearable
                options={createdDateOptions}
                placeholder="Any created date"
                onChange={filters.handleCreatedDateChange}
              />
            </Box>
          </Flex>
        </Flex>
      )}

      <Flex
        alignItems="center"
        direction="row"
        gap="4"
        my="4"
        justifyContent={{ base: "flex-end", md: "space-between" }}
      >
        {!isSmallScreen && (
          <InputGroup
            h="10"
            flex={{ base: "1", md: "none" }}
            w={{ md: "320px" }}
          >
            <InputLeftElement h="10" px="0" color="placeholder">
              <Icon as={HiOutlineSearch} boxSize="4" />
            </InputLeftElement>
            <Input
              value={filters.searchTerm}
              fontSize="sm"
              h="10"
              pr="12"
              placeholder="Filter by name"
              onChange={(event) => {
                filters.setSearchTerm(event.currentTarget.value);
              }}
            />
            <InputRightElement h="10" w="12">
              {filters.searchTerm && (
                <Icon
                  as={HiOutlineX}
                  boxSize="4"
                  ml="auto"
                  mr="2"
                  onClick={() => filters.setSearchTerm("")}
                />
              )}
            </InputRightElement>
          </InputGroup>
        )}
        <Flex alignItems="center" gap="2" flexShrink={0}>
          <Text fontSize="sm" color="gray.700">
            Showing
          </Text>
          <ChakraSelect
            h="10"
            fontSize="sm"
            w="20"
            value={filters.itemsToShow}
            onChange={(event) =>
              filters.setItemsToShow(event.currentTarget.value)
            }
            isDisabled={loading}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="150">150</option>
            <option value="200">200</option>
          </ChakraSelect>
        </Flex>
      </Flex>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
          <DrawerBody pt={6} pb={24}>
            <Stack spacing={5}>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={2}
                >
                  Keyword
                </FormLabel>
                <InputGroup h="10">
                  <InputLeftElement h="10" px="0" color="placeholder">
                    <Icon as={HiOutlineSearch} boxSize="4" />
                  </InputLeftElement>
                  <Input
                    value={filters.drawerKeyword}
                    fontSize="sm"
                    h="10"
                    pr="12"
                    placeholder="Filter by name"
                    onChange={(event) => {
                      filters.setDrawerKeyword(event.currentTarget.value);
                    }}
                  />
                  <InputRightElement h="10" w="12">
                    {filters.drawerKeyword && (
                      <Icon
                        as={HiOutlineX}
                        boxSize="4"
                        ml="auto"
                        mr="2"
                        onClick={() => filters.setDrawerKeyword("")}
                      />
                    )}
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={2}
                >
                  Status
                </FormLabel>
                <Select
                  theme={selectTheme}
                  styles={selectStyles}
                  value={drawerSelectedStatusOptions}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={statusOptions}
                  placeholder="All statuses"
                  onChange={filters.handleDrawerStatusChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={2}
                >
                  Position
                </FormLabel>
                <Select
                  theme={selectTheme}
                  styles={selectStyles}
                  value={drawerSelectedPositionOptions}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={positionSelectOptions}
                  placeholder="All positions"
                  onChange={filters.handleDrawerPositionChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={2}
                >
                  Author
                </FormLabel>
                <Select
                  theme={selectTheme}
                  styles={selectStyles}
                  value={drawerSelectedAuthorOptions}
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={authorSelectOptions}
                  placeholder="All authors"
                  onChange={filters.handleDrawerAuthorChange}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  mb={2}
                >
                  Created date
                </FormLabel>
                <Select
                  theme={selectTheme}
                  styles={selectStyles}
                  value={drawerSelectedCreatedDateOption}
                  isClearable
                  options={createdDateOptions}
                  placeholder="Any created date"
                  onChange={filters.handleDrawerCreatedDateChange}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={4}
            bg="white"
            borderTopWidth="1px"
            borderTopColor="gray.200"
          >
            <HStack spacing={3}>
              <Button
                flex="1"
                variant="outline"
                onClick={handleResetDrawerFilters}
              >
                Reset
              </Button>
              <Button
                flex="1"
                colorScheme="blue"
                onClick={handleApplyDrawerFilters}
              >
                Apply Filters
              </Button>
            </HStack>
          </Box>
        </DrawerContent>
      </Drawer>
    </>
  );
};
